<template>
  <v-container>
    <loader :isLoading="isLoading"></loader>
    <form-component
      ref="specimenForm"
      :formData="formData"
    >
      <template #additional-info-section>
        <div class="specimen-info-container">
          <label class="specimen-info-label"> Specimen Additional Info (If Applicable) </label>
          <nested-question
              v-if="caughtInfoValues.length"
              :display="'main'"
              :questionsMetadata="{
                  questionRawList: questionsMetadata.questionRawList,
                  questionList: questionsMetadata.questionList,
                  widgets: questionsMetadata.widgets,
                  parentWidget: questionsMetadata.widgets,
                  catchValue: questionsMetadata.catchValue,
              }"
            >
          </nested-question>
          <v-btn
            color="primary"
            dark
            block
            text
            class="add-info-button"
            @click="onClickAddInfo()"
          >
            <v-icon>mdi-plus</v-icon>
            Add Specimen info
          </v-btn>
        </div>
      </template>

      <template #protocol-number-section>
        <v-card class="mx-auto">
          <v-card-title>
            {{ protocolNumberTableMetadata.title }}
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              dark
              text
              class="mb-2"
              @click="onClickShowProtocolNumberSelection()"
            >
              <v-icon>mdi-plus</v-icon>
              {{ protocolNumberTableMetadata.addButtonName }}
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert type="error"
              v-for="(message, index) in warning"
              :key="`warning${index}`"
            >
              <span v-html="message"></span>
            </v-alert>
            <ul style="list-style-type: none; margin: 0px; padding: 0px;">
              <draggable
                handle=".handle"
                :list="protocolNumberTableMetadata.items"
              >
                <li
                  v-for="(item, index) in protocolNumberTableMetadata.items"
                  :key="`pn${index}`"
                >
                  <v-btn text class="handle"><v-icon>mdi-menu</v-icon></v-btn>
                  <span class="text">{{ item.protocolNumber }}</span>
                  <v-btn text class="close">
                    <v-icon
                      class="mr-2"
                      color="primary"
                      @click="onClickDelete(item)"
                    >
                      mdi-delete
                    </v-icon>
                  </v-btn>
                </li>
              </draggable>
            </ul>
          </v-card-text>
        </v-card>
      </template>
      <template #save-button-section>
        <v-btn
            style="margin-top: 20px;"
            color='primary'
            depressed
            clearable
            type="button"
            :disabled="disableSubmitBtn"
            @click="onClickSpecimenSave()"
          >
            Save
        </v-btn>
      </template>
    </form-component>

     <!-- protocol number drawer  -->
     <v-navigation-drawer
      v-model="isSelectingProtocolNumbers"
      fixed
      right
      temporary
      width="650px"
    >
      <v-list-item>
        <h3>{{ navDrawerSelectionMetadata.protocolNumber.title }}</h3>
      </v-list-item>

      <v-divider></v-divider>

      <v-col cols="12">
        <v-card>
          <v-card-title>
            {{ navDrawerSelectionMetadata.protocolNumber.tableTitle }}
            <v-spacer></v-spacer>
            <v-text-field
              v-model="navDrawerSelectionMetadata.protocolNumber.search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
            v-model="navDrawerSelectionMetadata.protocolNumber.selected"
            :headers="navDrawerSelectionMetadata.protocolNumber.headers"
            :items="navDrawerSelectionMetadata.protocolNumber.items"
            :items-per-page="5"
            :search="navDrawerSelectionMetadata.protocolNumber.search"
            item-key="_id"
            show-select
            single-select
            class="elevation-1"
          >
            <template v-slot:[`item.actions`]="{ item }">
              <v-icon
                class="mr-2"
                @click="onClickViewFormPreview(item)"
                color="blue"
              >
                mdi-eye
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
        <v-btn
          style="margin-top: 20px;"
          color="primary"
          @click="onClickAddSelectedProtocolNumberItems"
        >
          Add
        </v-btn>
      </v-col>
    </v-navigation-drawer>
  </v-container>
</template>

<!-- eslint-disable no-underscore-dangle -->
<script>
import draggable from 'vuedraggable';
import { Subject } from 'rxjs';

// https://github.com/ejcubillas/phil-reg-prov-mun-brgy?tab=readme-ov-file
import phil from 'phil-reg-prov-mun-brgy';

import Loader from '../../../components/utilities/Loader.vue';
import FormComponent from '../../../components/ui/FormComponent.vue';
import NestedQuestion from './components/NestedQuestion.vue';

import FormService from '../../../services/medical_registry/form.service';

import services from '../../../services';

const { biobankRegistry, medicalRegistry, collectingInstitutionRegistry } = services;
const {
  specimenTypeService,
  specimenStateService,
  methodOfCollectionService,
  freezerService,
  caseSpecimenService,
  specimenAdditionalInfoService,
  boxService,
} = biobankRegistry;

const {
  specimenSourceService,
  caseService,
  protocolNumberService,
  // formService,
} = medicalRegistry;

const { collectingInstitutionService } = collectingInstitutionRegistry;

export default {
  name: 'SpecimenForm',
  components: {
    FormComponent,
    Loader,
    draggable,
    NestedQuestion,
  },
  props: {
    onSubmit: Function,
    isLoading: Boolean,
    specimenCase: Object,
    // surveyForm: Object,
  },
  data() {
    return {
      freezerData: {},
      boxLocationInitial: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'],
      isSelectingProtocolNumbers: false,
      philRegions: [],
      philProvinces: [],
      specimenSourceDefault: {
        // default specimen source value 'Human'
        _id: '651b63d7831587428f2d5cf1',
      },
      emptyCaseNumber: {
        label: 'None',
        code: '',
      },
      emptySpecimenSource: {
        label: 'Not Applicable',
        code: null,
      },
      warning: {},
      caseNumberData: {},
      disableSubmitBtn: false,
      selectedQuestion: '',
      formData: {
        onSubmit: this.onSubmit,
        model: this.specimenCase,
        widgets: [
          {
            type: 'text',
            name: 'code',
            label: 'Specimen Code',
            value: '',
            counter: 100,
            maxLength: 100,
            rules: [
              (v) => !!v || 'Sample Code is required',
            ],
          },
          {
            type: 'select',
            name: 'specimenSource', // name will change depending on existing specimen // caseNumberSpecimenSource
            label: 'Specimen Source',
            ref: 'specimenSourceInput',
            placeholder: 'Select where the specimen came from',
            readonly: false,
            multiple: false,
            items: [],
            // rules: [
            //   (v) => !!v || 'Specimen Source is required',
            // ],
            value: '',
          },
          {
            type: 'select', // display all data first
            name: 'caseNumber',
            label: 'Case Number',
            placeholder: 'Select Case Number',
            multiple: false,
            readonly: false,
            items: [],
            value: '',
          },
          {
            type: 'select',
            name: 'sampleFrom',
            label: 'Method of Collection',
            placeholder: 'Select Method of collection',
            multiple: false,
            rules: [
              (v) => !!v || 'Method of collection is required',
            ],
            items: [],
            value: '',
          },
          {
            type: 'select',
            name: 'specimenType',
            label: 'Specimen Type',
            placeholder: 'Select where the specimen came from',
            multiple: false,
            rules: [
              (v) => !!v || 'Specimen Type is required',
            ],
            items: [
              {
                label: 'Blood',
                code: '630cf042e5b54f0138f7b1cd',
              },
              {
                label: 'Urine',
                code: '632d96ba43c7bf920b027833',
              },
            ],
            value: '',
          },
          {
            type: 'select',
            name: 'specimenState',
            label: 'Preparation',
            placeholder: 'Select preparation',
            multiple: false,
            rules: [
              (v) => !!v || 'Preparation is required',
            ],
            items: [
              {
                label: 'Frozen',
                code: '630ccdf89eb0efca5a34e01b',
              },
            ],
            value: '',
          },
          {
            type: 'select',
            name: 'boxNumber',
            label: 'Box Number',
            multiple: false,
            // selectChange: (selectedValue) => this.setBoxNumbers(selectedValue),
            rules: [
              (v) => !!v || 'Box Number is required',
            ],
            items: [],
            value: '',
          },
          {
            type: 'select',
            name: 'freezer',
            label: 'Select Freezer',
            // selectChange: (data) => this.testFreezer(data),
            value: '',
            items: [],
            readonly: true,
          },
          {
            type: 'text',
            name: 'temperature',
            label: 'Temperature',
            value: '',
            readonly: true,
          },
          {
            type: 'text',
            name: 'shelfNumber',
            label: 'Shelf Number',
            multiple: false,
            // selectChange: () => this.setBoxNumbers(),
            rules: [
              (v) => !!v || 'Shelf Number is required',
            ],
            items: [],
            value: '',
            readonly: true,
          },
          {
            // type: 'number',
            type: 'text',
            name: 'rackNumber',
            label: 'Rack Number',
            multiple: false,
            // selectChange: () => this.setBoxNumbers(),
            rules: [
              (v) => !!v || 'Rack Number is required',
            ],
            items: [],
            value: '',
            readonly: true,
          },
          {
            type: 'text',
            name: 'slidingRackNumber',
            label: 'Sliding Rack Number',
            multiple: false,
            placeholder: '',
            // selectChange: () => this.setBoxNumbers(),
            rules: [
              (v) => !!v || 'Sliding Rack Number is required',
            ],
            items: [],
            value: '',
            readonly: true,
          },
          {
            type: 'select',
            name: 'boxLocation',
            label: 'Box Location',
            multiple: false,
            placeholder: '',
            rules: [
              (v) => !!v || 'Box Number is required',
            ],
            items: [],
            value: '',
          },
          // {
          //   type: 'text',
          //   name: 'boxLocation',
          //   label: 'Site of Collection',
          //   value: '',
          //   counter: 250,
          //   maxLength: 250,
          // },
          {
            type: 'select',
            name: 'siteOfCollection',
            label: 'Collecting Institution',
            counter: 200,
            maxLength: 200,
            items: [],
            value: '',
          },
          {
            type: 'text', // display all data first
            name: 'region',
            label: 'Site of Collection (Region)',
            placeholder: 'Select a Collecting Institution first',
            readonly: true,
            value: '',
          },
          {
            type: 'text', // display all data first
            name: 'province',
            label: 'Site of Collection (Province)',
            placeholder: 'Select a Collecting Institution first',
            readonly: true,
            value: '',
          },
          {
            type: 'date',
            label: 'Date of Collection',
            name: 'dateOfCollection',
            placeholder: 'Define the date of collection',
            customStyle: {
              margin: '-10px 0px 20px 0px',
            },
            value: '',
            range: false,
          },
          {
            type: 'file',
            name: 'barcodeAttachment',
            label: 'Barcode Upload',
            multiple: false,
            accept: '.png',
            imageURL: null,
            value: null,
          },
          {
            type: 'anchor',
            id: 'additional-info-section',
          },
          {
            type: 'textarea',
            name: 'note',
            label: 'note',
            value: '',
            counter: 500,
            maxLength: 500,
            rules: [],
          },
          {
            type: 'anchor',
            id: 'protocol-number-section',
          },
          {
            type: 'anchor',
            id: 'save-button-section',
          },
        ],
      },
      protocolNumberTableMetadata: {
        search: '',
        title: 'Project Code',
        addButtonName: 'Add Project Code',
        headers: [
          { text: 'Project Code', value: 'protocolNumber' },
          { text: 'ID', value: '_id' },
          { text: 'Actions', value: 'actions', sortable: false },
        ],
        items: [],
        value: {},
      },
      navDrawerSelectionMetadata: {
        protocolNumber: {
          search: '',
          title: 'Select One Project Code to use',
          tableTitle: 'Project Code',
          headers: [
            { text: 'Project Code', value: 'protocolNumber' },
            { text: 'ID', value: '_id' },
            { text: 'Actions', value: 'actions', sortable: false },
          ],
          items: [],
          selected: [],
        },
      },
      caughtInfoValues: [],
      questionsMetadata: {
        questionRawList: [],
        questionList: [],
        widgets: [],
      },
      questionListSubject: new Subject(),
    };
  },
  async created() {
    const caseNumberWidget = this.formData
      .widgets
      .find((w) => w.name === 'caseNumber');
    const specimenSourceWidget = this.formData
      .widgets
      .find((w) => w.name === 'specimenSource');

    const protocolNumbers = await protocolNumberService.getList();
    this.navDrawerSelectionMetadata.protocolNumber.items = protocolNumbers;

    // set the regions here.

    this.philRegions = phil.regions;
    this.philProvinces = phil.provinces;

    const prepareSpecimen = async ({ name, dataSource }) => {
      const data = await dataSource();
      const widget = this.formData.widgets.find((w) => w.name === name);
      widget.items = data.map((doc) => ({ code: doc._id, label: doc.name }));
    };

    prepareSpecimen({ name: 'specimenType', dataSource: () => specimenTypeService.getList() });
    prepareSpecimen({ name: 'specimenState', dataSource: () => specimenStateService.getList() });
    prepareSpecimen({ name: 'sampleFrom', dataSource: () => methodOfCollectionService.getList() });
    prepareSpecimen({ name: 'freezer', dataSource: () => freezerService.getList() });
    prepareSpecimen({ name: 'siteOfCollection', dataSource: () => collectingInstitutionService.getList() });
    prepareSpecimen({ name: 'boxNumber', dataSource: () => boxService.getList() });

    const specimenSourceData = await specimenSourceService.getList();
    specimenSourceWidget.items = specimenSourceData
      .map((doc) => ({ code: doc._id, label: doc.name }));
    specimenSourceWidget.items.push(this.emptySpecimenSource);
    // specimenSourceWidget.value = this.specimenSourceDefault._id;
    specimenSourceWidget.value = this.emptySpecimenSource.code;

    const cases = await caseService.getList();

    caseNumberWidget.items = cases
      .map((document) => ({ code: document._id, label: document.caseNumber }));
    caseNumberWidget.items.push(this.emptyCaseNumber);

    if (this.formData.model) {
      const { protocolNumber } = this.formData.model;

      // make this specific, get one field only
      // this.formData.widgets.forEach((w) => {
      //   const widget = w;
      //   const getValue = (key) => {
      //     if (key.includes('date')) {
      //       return new Date(this.formData.model[key]);
      //     }
      //     return this.formData.model[widget.name];
      //   };
      //   widget.value = getValue(widget.name);
      // });

      this.formData.widgets.map((fd) => {
        if (fd.type === 'date') {
          fd.value = new Date(fd.value);
        }
        if (this.formData.model.location[fd.name]) {
          fd.value = this.formData.model.location[fd.name];
        }
        return fd;
      });

      if (!this.formData.model.caseNumber) {
        caseNumberWidget.value = this.emptyCaseNumber.code;
      }

      // if (specimenSource?._id) {
      //   specimenSourceWidget.value = specimenSource._id;
      // }
      if (protocolNumber) {
        const existingProtocol = protocolNumbers
          .find((protocol) => protocol._id === protocolNumber?._id);
        this.protocolNumberTableMetadata.items.push(existingProtocol);
        this.protocolNumberTableMetadata.value = existingProtocol;
      }

      if (this.formData.model.barcode?._id) {
        const barcodeAttachment = this.formData.widgets.find(
          ({ name }) => name === 'barcodeAttachment',
        );
        barcodeAttachment.label = this.formData.model.barcode
          ? `${this.formData.model.barcode?._id}.png`
          : '';
        const { barcode } = await caseSpecimenService.getOne({ id: this.formData.model._id });
        barcodeAttachment.imageURL = barcode.url;
      }
    }
    // get the specimen additional info
    const questions = await specimenAdditionalInfoService.getList();

    this.questionsMetadata.questionRawList = questions;
    const selections = questions.map((q) => {
      const { _id, name, type } = q;
      return {
        label: name,
        code: _id,
        icon: FormService.getQuestionTypeIcon(type),
      };
    });
    this.questionsMetadata.questionList = selections;
    this.questionListSubject.next(selections);
    // for view only
    const { specimenAdditionalInfo } = this.formData.model;
    if (specimenAdditionalInfo.length > 0) {
      const qParent = this.questionsMetadata.widgets;
      const goToQuestions = (question, index, parent) => {
        const { display, _id } = question;
        const widget = this.getWidget(display, parent, index);
        if (display === 'question') {
          this.onSelectChangeDisableItem(_id, null, 'init of widget');
          const questionJSON = questions.find((q) => {
            const { _id: qId } = q;
            return qId === _id;
          });
          widget.value = _id;
          widget.preview = {
            ...FormService.getValidWidgetJSON(questionJSON),
            readonly: false,
            // populate the value here
          };
          parent.push(widget);
          const matchedInfo = specimenAdditionalInfo
            .find((addedInfo) => addedInfo._id === widget.value);
          const { preview } = widget;
          preview.value = matchedInfo.answer;
        }
      };
      this.formData.model.specimenAdditionalInfo.forEach(
        (question, index) => goToQuestions(question, index, qParent),
      );
    } else {
      const basedArray = ['question'];
      basedArray.forEach((type, index) => {
        const parent = this.questionsMetadata.widgets;
        const widget = this.getWidget(type, parent, index);
        this.questionsMetadata.widgets.push(widget);
      });
    }
  },
  watch: {
    questionsMetadata: {
      handler(newVal) {
        // apply this method to this function to enable and disable the question selection
        // onSelectChangeDisableItem(code, oldVal, source)

        // Perform some action when questionsMetadata changes
        const { widgets } = newVal;
        console.log('widgets', widgets);
        const infosArray = [];
        widgets.forEach((w) => {
          const { preview } = w;
          const {
            name,
            label,
            value,
          } = preview;
          // if else new val and oldval apply the onSelectChangeDisableItem
          // this.onSelectChangeDisableItem(w.value, null, 'onSelectQuestion');
          infosArray.push({
            _id: w.value,
            name: label,
            code: name,
            type: preview.type,
            display: 'question',
            answer: value,
          });
        });
        // oldVal.widgets.forEach((ow) =>
        // this.onSelectChangeDisableItem(null, ow.value, 'onSelectQuestion'));
        this.caughtInfoValues = infosArray;
        console.log('infosArray', infosArray);
      },
      deep: true, // Watch nested changes within questionsMetadata
    },
    // eslint-disable-next-line object-shorthand
    async 'formData.widgets.2.value'(id) {
      // get the case number data
      this.caseNumberData = id && await caseService.getOne({ id });
      setTimeout(async () => {
        await this.onChangeCaseNumber(this.caseNumberData);
      }, 700);
    },
    // eslint-disable-next-line
    'protocolNumberTableMetadata.value': function () {
      setTimeout(async () => {
        await this.onChangeCaseNumber(this.caseNumberData);
      }, 700);
    },
    // eslint-disable-next-line object-shorthand
    async 'formData.widgets.6.value'(boxId) {
      // to render boxes
      this.setBoxNumbers(boxId);
    },
    // eslint-disable-next-line object-shorthand
    async 'formData.widgets.13.value'(id) {
      await this.setInstitutionRegProv(id);
    },
  },
  methods: {
    async setInstitutionRegProv(id) {
      const institutionDetails = await collectingInstitutionService.getOne({ id });

      const regionsWidget = this.getFormValue('region');
      const provinceWidget = this.getFormValue('province');

      regionsWidget.value = this.philProvinces
        .find((province) => province.prov_code === institutionDetails.province)?.name;
      provinceWidget.value = this.philRegions
        .find((region) => region.reg_code === institutionDetails.region)?.name;
    },
    noSpecialCharsRule(value) {
      const pattern = /^[A-Za-z0-9\s]+$/;
      if (!pattern.test(value)) {
        return 'This field should not contain special characters.';
      }
      return true;
    },
    async onChangeCaseNumber(caseData) {
      // get the specimen source properties using Ref
      const specimenSourceWidget = this.formData
        .widgets
        .find((w) => w.ref === 'specimenSourceInput');

      specimenSourceWidget.readonly = !!caseData;
      specimenSourceWidget.type = caseData ? 'text' : 'select';

      // get case number source
      const caseExistingSource = caseData
        && await specimenSourceService.getOne({
          id: caseData.specimenSource,
        });
      // set existing data
      specimenSourceWidget.value = caseExistingSource?.name
        || this.formData.model?.specimenSource?._id
        || this.emptySpecimenSource.code;

      this.onChangeCaseProtocol(caseData);
    },
    async onClickSpecimenSave() {
      const { specimenForm } = this.$refs;
      console.log('this.$refs', this.$refs);
      if (!specimenForm.validate()) {
        return;
      }
      if (this.protocolNumberTableMetadata.items.length > 0) {
        const dataChanges = FormService.dataAsJSON(this.formData);
        dataChanges.protocolNumber = {
          _id: this.protocolNumberTableMetadata.value._id,
        };
        const caseSpecimen = specimenForm.dataAsJSON();
        console.log('caseSpecimen', caseSpecimen);
        caseSpecimen.location = {
          // name ang nakukuha imbis na ID pumapasok sa freezer 4/10/25 6:45 pm
          freezer: caseSpecimen.freezer,
          temperature: caseSpecimen.temperature,
          shelfNumber: caseSpecimen.shelfNumber,
          rackNumber: caseSpecimen.rackNumber,
          slidingRackNumber: caseSpecimen.slidingRackNumber,
          boxLocation: caseSpecimen.boxLocation,
          boxNumber: caseSpecimen.boxNumber,
        };
        console.log('freezer onSave', caseSpecimen.freezer);
        const data = Object.assign(caseSpecimen || {}, dataChanges);
        if (caseSpecimen?.caseNumber) {
          const assignObjectCase = {
            caseNumber: { _id: caseSpecimen.caseNumber },
          };
          // empty specimen source when there is case number
          assignObjectCase.specimenSource = null;
          Object.assign(data || {}, assignObjectCase);
        }
        // debug here also
        data.specimenAdditionalInfo = this.caughtInfoValues;
        this.onSubmit(data);
      } if (this.protocolNumberTableMetadata.items.length === 0) {
        this.$confirmDialog.show({
          title: 'Save Error',
          text: `
            <ul>
              <li>Select one project code</li>
            </ul>
          `,
          isMessage: true,
        });
      }
    },
    async onChangeCaseProtocol(caseData) {
      const { value: protocolValue } = this.protocolNumberTableMetadata;
      let protocolNumbers = [];

      if (!caseData) { // None
        protocolNumbers = await protocolNumberService.getList();
      }

      if (caseData) { // with case number
        protocolNumbers = await Promise.all(
          caseData?.protocolNumbers.map(async (c) => (
            protocolNumberService.getOne({ id: c.id })
          )),
        );
      }

      // remove protocol table items if not in case
      const isExistProtocol = caseData && caseData.protocolNumbers
        .find((p) => p.id === protocolValue._id);
      if (!isExistProtocol && caseData && protocolValue._id) {
        this.disableSubmitBtn = true;
        this.$set(this.warning,
          'protocol',
          `Project code must be associated with case number <b>${caseData.caseNumber}</b>`);
      } else {
        this.disableSubmitBtn = false;
        this.$delete(this.warning, 'protocol');
      }

      this.navDrawerSelectionMetadata.protocolNumber.items = protocolNumbers;
    },
    onClickShowProtocolNumberSelection() {
      this.isSelectingProtocolNumbers = true;
    },
    onClickAddSelectedProtocolNumberItems() {
      const { selected } = this.navDrawerSelectionMetadata.protocolNumber;
      this.navDrawerSelectionMetadata.protocolNumber.selected = [];
      selected.forEach((form) => {
        const isPresent = this.protocolNumberTableMetadata.items
          .find((f) => f._id === form._id);
        if (!isPresent) {
          this.protocolNumberTableMetadata.items = [];
          this.protocolNumberTableMetadata.items.push(form);
          this.protocolNumberTableMetadata.value = form;
        }
      });
      this.isSelectingProtocolNumbers = false;
    },
    onClickDelete(item) {
      this.$confirmDialog.show({
        text: `<p>Are you sure you want to delete: <b>${item.protocolNumber}</b></p>`,
        title: 'Confirm Delete',
      }, () => {
        const tableMetaData = this.protocolNumberTableMetadata;

        const filtered = tableMetaData.items
          .filter((data) => data._id !== item._id);
        tableMetaData.items = filtered;
        tableMetaData.value = {};

        this.$snackbar.show({ color: 'green', text: 'Deleted successfully', closeColor: 'white' });
      });
    },
    // async displayTemperature() {
    //   this.getFormValue('freezer');
    //   this.getFormValue('temperature');
    //   const { temperature } = await this.getFreezerData(freezer.value);
    //   console.log('displayTemperature', temperature);
    //   console.log('temperatureField', temperatureField);
    //   temperatureField.value = temperature;
    // },
    async getFreezerData(freezerId) {
      return freezerService.getOne({
        id: freezerId,
      });
    },
    getFormValue(inputName) {
      return this.formData
        .widgets
        .find(({ name }) => name === inputName);
    },
    onClickAddInfo() {
      const qParent = this.questionsMetadata.widgets;
      // qParent.display = 'section';
      this.questionsMetadata.widgets.forEach((widgets) => {
        widgets.preview.readonly = false;
      });

      const widget = this.widgetQuestion({
        questions: this.questionsMetadata.questionRawList,
        selections: this.questionsMetadata.questionList,
        parent: qParent,
        index: qParent.length,
        selectChange: (value, oldVal) => this.onSelectChangeDisableItem(value, oldVal, 'onClickAdd'),
      });
      this.questionsMetadata.widgets.push(widget);
    },
    // eslint-disable-next-line no-unused-vars
    onSelectChangeDisableItem(code, oldVal, source) {
      this.disableSelection(code, true);
      if (oldVal) {
        this.disableSelection(oldVal, false);
      }
    },
    getWidget(type, parent, index) {
      if (type === 'section') {
        return this.widgetQuestion({
          parent,
          index,
        });
      }
      return this.widgetQuestion({
        questions: this.questionsMetadata.questionRawList,
        selections: this.questionsMetadata.questionList,
        parent,
        index,
        selectChange: (value, oldVal) => this.onSelectChangeDisableItem(value, oldVal, 'selectChange'),
      });
    },
    disableSelection(code, enabled) {
      const item = this.questionsMetadata.questionList.find((q) => q.code === code);
      if (enabled) {
        item.disabled = true;
      } else {
        delete item.disabled;
      }
    },
    widgetQuestion({
      questions, selections, parent, index, selectChange,
    }) {
      const widget = {
        type: 'select',
        display: 'question',
        name: 'selectedQuestion',
        label: 'Question',
        placeholder: 'Enter question',
        parent,
        index,
        conditionalWidgets: {
          isRequired: {
            type: 'switch',
            name: 'isRequired',
            label: 'Mandatory',
            value: true,
          },
        },
        multiple: false,
        value: '',
        preview: {},
        items: selections,
        rules: [
          (v) => !!v || 'Question type is required',
        ],
      };
      // eslint-disable-next-line func-names
      widget.selectChange = function (code, oldValue) {
        if (code) {
          const question = questions.find((q) => {
            const { _id } = q;
            return _id === code;
          });
          this.preview = { ...FormService.getValidWidgetJSON(question), readonly: false };
        } else {
          this.preview = {};
        }
        selectChange(code, oldValue);
      };
      return widget;
    },
    setNumberToFormSelect(formName, number) {
      const formProperties = this.getFormValue(formName);
      for (let i = 1; i <= number; i += 1) {
        formProperties.items.push({ code: i, label: i });
      }
    },
    async setBoxNumbers(boxData) {
      // onchange the freezer and other
      const freezers = await freezerService.getList();
      const freezerValue = this.getFormValue('freezer');
      const shelfValue = this.getFormValue('shelfNumber');
      const rackValue = this.getFormValue('rackNumber');
      const slidingRackValue = this.getFormValue('slidingRackNumber');
      const temperatureValue = this.getFormValue('temperature');

      let found = false;

      await Promise.all(
        freezers.map(async (fr) => {
          if (found || !Array.isArray(fr.boxNumbers)) return;

          const match = fr.boxNumbers.find((bn) => bn.boxId === boxData);

          if (match) {
            const { boxLocation } = match;
            freezerValue.value = fr._id;
            temperatureValue.value = fr.temperature;
            shelfValue.value = boxLocation.shelfNo;
            rackValue.value = boxLocation.rackNo;
            slidingRackValue.value = boxLocation.slidingRackNo;

            const box = await boxService.getOne({ id: boxData });
            this.setBoxLocations1(box);

            found = true;
          }
        }),
      );

      // If not found, clear all values
      if (!found) {
        freezerValue.value = null;
        temperatureValue.value = null;
        shelfValue.value = null;
        rackValue.value = null;
        slidingRackValue.value = null;
      }
    },
    setBoxLocations1(data) {
      const boxLocationProps = this.getFormValue('boxLocation');
      const storeBoxloc = [];
      for (let i = 1; i <= data.noOfColumn; i += 1) {
        for (let y = 1; y <= data.noOfRows; y += 1) {
          const t = this.boxLocationInitial[i - 1];
          storeBoxloc.push({
            code: t + y, // ex: A1, A2...
            label: t + y,
          });
        }
      }
      boxLocationProps.items = storeBoxloc;
    },
  },
};
</script>

<style scoped>
.close {
  float: right;
  padding-top: 8px;
  padding-bottom: 8px;
}
.text {
  font-weight: bold;
  margin: 20px;
}
.specimen-info-label {
  font-size: 16px;
  font-weight: bold;
  color: #333; /* Darker text color for readability */
  margin-bottom: 8px; /* Space below the label */
  display: block;
}
.specimen-info-container {
  background-color: #f9f9f9; /* Soft background color */
  border: 1px solid #ddd; /* Light border for separation */
  border-radius: 6px; /* Rounded corners */
  padding: 12px 16px; /* Inner padding */
  margin-top: 10px; /* Space above the section */
  margin-bottom: 40px;
}
.add-info-button {
  color: #8b0000; /* Dark red color for the button text */
  display: inline-block;
  margin-top: 8px;
  cursor: pointer;
  text-decoration: none;
}
</style>
